import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Zap, LogOut, Upload, Loader2, Plus, Pencil, Trash2, Eye, EyeOff, GripVertical,
  ExternalLink, Copy, Rocket, Undo2, Sparkles, ImageDown,
  LayoutGrid, BarChart3, Users, Settings as SettingsIcon, X as XIcon,
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLinkitAuth } from "../AuthContext";
import { linkitApi, formatApiErrorDetail, uploadLinkitImage, resolveImg, resolveSocialAvatar } from "../api";
import { BLOCK_TYPES, FONT_OPTIONS } from "../blockDefs";
import PagePreview from "../components/PagePreview";
import BlockEditor from "../components/BlockEditor";
import AIAssistant from "../components/AIAssistant";
import LinkitAnalytics from "../components/AnalyticsPanel";
import LeadsPanel from "../components/LeadsPanel";
import SettingsPanel from "../components/SettingsPanel";
import ImageCropperModal from "../../components/ImageCropperModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

function BlockRow({ block, onEdit, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const def = BLOCK_TYPES[block.type];
  const Icon = def?.icon || Plus;
  const title = block.label || block.name || block.heading || block.title || def?.label || block.type;
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className="flex items-center gap-2 rounded-xl border border-black/10 bg-white p-2.5">
      <button {...attributes} {...listeners} data-testid={`block-drag-${block.id}`} className="cursor-grab touch-none rounded p-1 text-black/30 hover:text-black/60 active:cursor-grabbing"><GripVertical size={16} /></button>
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><Icon size={15} /></span>
      <div className="min-w-0 flex-1">
        <p className={`truncate text-sm font-semibold ${block.hidden ? "text-black/40 line-through" : ""}`}>{title}</p>
        <p className="text-[11px] uppercase tracking-wide text-black/40">{def?.label || block.type}</p>
      </div>
      <button onClick={onToggle} title={block.hidden ? "Show" : "Hide"} className="rounded-lg p-2 hover:bg-black/5">{block.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
      <button onClick={onEdit} data-testid={`block-edit-${block.id}`} className="rounded-lg p-2 hover:bg-black/5"><Pencil size={14} /></button>
      <button onClick={onDelete} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
    </div>
  );
}

function ColorInput({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-black/60">{label}</span>
      <div className="flex items-center gap-2">
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="w-24 rounded-lg border border-white/10 bg-white/10 px-2 py-1 text-xs text-white" />
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-7 w-8 cursor-pointer rounded border border-black/10" />
      </div>
    </div>
  );
}

// Warm nudge for unverified accounts. Never blocks the app — the user can keep
// building; verification is a hygiene step for future SaaS lifecycle features.
function VerifyBanner() {
  const { user, refresh } = useLinkitAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try {
      await linkitApi.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
      // Try to refresh so if the user verifies in another tab, the banner disappears.
      try { await refresh(); } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="verify-banner" className="flex flex-col gap-2 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100 sm:flex-row sm:items-center">
      <div className="flex-1 text-xs sm:text-sm">
        <span className="font-bold">Verify your email</span> <span className="text-amber-100/80">— confirm <span className="font-semibold">{user.email}</span> so you don't lose access to your page.</span>
      </div>
      <button
        onClick={resend}
        disabled={busy}
        data-testid="verify-resend"
        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-amber-400/90 px-3 py-1.5 text-xs font-bold text-amber-950 hover:bg-amber-300 disabled:opacity-60"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : null} Resend link
      </button>
    </div>
  );
}

// "Linkit is on iOS + Android" nudge — one-time, dismissable via localStorage.
// Kept small (single row) so it never gets in the way of the actual editor.
function MobileAppStrip() {
  const [hidden, setHidden] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem("linkit.mobile_strip_dismissed") === "1"; }
    catch { return false; }
  });
  if (hidden) return null;
  const dismiss = () => {
    try { window.localStorage.setItem("linkit.mobile_strip_dismissed", "1"); } catch { /* noop */ }
    setHidden(true);
  };
  // Placeholder store URLs — swap once you have the real listings live.
  const APP_STORE = "https://apps.apple.com/app/linkit/id0000000000";
  const PLAY_STORE = "https://play.google.com/store/apps/details?id=app.linkit.creator";
  return (
    <div
      data-testid="linkit-mobile-strip"
      className="relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-r from-[#FF7A70]/15 via-[#FF5AA0]/10 to-[#A78BFA]/10 p-4 sm:flex-row sm:items-center"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF7A70] text-lg font-black text-black">L</div>
        <div>
          <p className="text-sm font-bold text-white">Linkit is on iOS &amp; Android</p>
          <p className="text-xs text-white/60">Edit, preview and publish your page from your phone.</p>
        </div>
      </div>
      <div className="flex flex-1 flex-wrap items-center gap-2 sm:justify-end">
        <a
          href={APP_STORE} target="_blank" rel="noreferrer"
          data-testid="linkit-download-ios"
          className="inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-2 text-[11px] font-bold text-white ring-1 ring-white/20 hover:ring-white/40"
        >
          <svg width="14" height="14" viewBox="0 0 384 512" fill="currentColor"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM254 84.7q34.5-40.9 31.3-93.1-45.2 3.7-83.4 41.9-37.4 32.2-31.4 87.3 50.2 3.9 83.5-36.1z"/></svg>
          App Store
        </a>
        <a
          href={PLAY_STORE} target="_blank" rel="noreferrer"
          data-testid="linkit-download-android"
          className="inline-flex items-center gap-1.5 rounded-full bg-black px-4 py-2 text-[11px] font-bold text-white ring-1 ring-white/20 hover:ring-white/40"
        >
          <svg width="14" height="14" viewBox="0 0 512 512" fill="currentColor"><path d="M99.617 8.057a50.191 50.191 0 00-38.815-6.713l230.932 230.933 74.846-74.846L99.617 8.057zM32.139 20.116c-6.441 8.563-10.148 19.077-10.148 30.199v411.358c0 11.123 3.708 21.636 10.148 30.199l235.877-235.877L32.139 20.116zM464.261 212.087l-67.266-37.637-81.544 81.544 81.544 81.544 67.266-37.637a49.804 49.804 0 000-87.814zM291.733 279.711L60.815 510.629c12.859 3.451 26.803 1.63 38.802-5.393l305.995-171.194-113.879-54.331z"/></svg>
          Google Play
        </a>
        <button
          onClick={dismiss}
          data-testid="linkit-mobile-strip-dismiss"
          className="rounded-full p-1.5 text-white/50 hover:bg-white/5 hover:text-white/80"
          aria-label="Dismiss"
        >
          <XIcon size={14} />
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const { user, ready, logout } = useLinkitAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // block being edited
  const [addOpen, setAddOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarCropSrc, setAvatarCropSrc] = useState(null);
  const [grabUrl, setGrabUrl] = useState("");
  const [grabbing, setGrabbing] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [tab, setTab] = useState("editor"); // editor | analytics | leads | settings
  const avatarRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const content = page?.content;

  useEffect(() => { if (ready && !user) nav("/login", { replace: true }); }, [ready, user, nav]);

  const load = useCallback(async () => {
    try {
      const { data } = await linkitApi.get("/page");
      setPage(data);
    } catch (e) { toast.error("Could not load your page"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  // ---- profile (debounced save)
  const [profile, setProfile] = useState({ display_name: "", bio: "", avatar_url: "" });
  useEffect(() => { if (content?.profile) setProfile(content.profile); }, [page?.username]);
  const profileTimer = useRef(null);
  const saveProfile = (next) => {
    setProfile(next);
    clearTimeout(profileTimer.current);
    profileTimer.current = setTimeout(async () => {
      try { const { data } = await linkitApi.put("/page/profile", next); setPage(data); } catch { toast.error("Save failed"); }
    }, 500);
  };
  const pickAvatar = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAvatarCropSrc(URL.createObjectURL(file));
    e.target.value = "";
  };
  const editExistingAvatar = async () => {
    if (!profile.avatar_url) { avatarRef.current?.click(); return; }
    try {
      const res = await fetch(resolveImg(profile.avatar_url), { credentials: "omit" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      setAvatarCropSrc(URL.createObjectURL(blob));
    } catch { toast.error("Couldn't load photo. Upload a new one instead."); avatarRef.current?.click(); }
  };
  const onAvatarCropped = async (blob) => {
    setAvatarCropSrc(null);
    setAvatarBusy(true);
    try { const url = await uploadLinkitImage(new File([blob], `dp-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" })); saveProfile({ ...profile, avatar_url: url }); }
    catch { toast.error("Upload failed"); } finally { setAvatarBusy(false); }
  };
  const grabAvatar = async () => {
    if (!grabUrl.trim() || grabbing) return;
    setGrabbing(true);
    try {
      const url = await resolveSocialAvatar(grabUrl.trim());
      saveProfile({ ...profile, avatar_url: url });
      setGrabUrl("");
      toast.success("Photo grabbed from your link");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setGrabbing(false); }
  };

  // ---- theme
  const saveTheme = async (patch) => {
    try { const { data } = await linkitApi.put("/page/theme", patch); setPage(data); } catch { toast.error("Save failed"); }
  };
  const theme = content?.theme || {};

  // ---- blocks
  const addBlock = async (type) => {
    setAddOpen(false);
    try {
      const { data } = await linkitApi.post("/page/blocks", { type, data: BLOCK_TYPES[type].defaults });
      setPage(data);
      const newBlock = data.content.blocks[data.content.blocks.length - 1];
      setEditing(newBlock);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveBlock = async (formData) => {
    try {
      const { data } = await linkitApi.put(`/page/blocks/${editing.id}`, { type: editing.type, ...formData });
      setPage(data); setEditing(null); toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };
  const deleteBlock = async (id) => {
    if (!window.confirm("Delete this block?")) return;
    const { data } = await linkitApi.delete(`/page/blocks/${id}`); setPage(data);
  };
  const toggleBlock = async (b) => {
    const { data } = await linkitApi.put(`/page/blocks/${b.id}`, { type: b.type, hidden: !b.hidden, ...Object.fromEntries(Object.entries(b).filter(([k]) => !["id", "type", "hidden"].includes(k))) });
    setPage(data);
  };
  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const blocks = content.blocks;
    const next = arrayMove(blocks, blocks.findIndex((b) => b.id === active.id), blocks.findIndex((b) => b.id === over.id));
    setPage((p) => ({ ...p, content: { ...p.content, blocks: next } }));
    try { const { data } = await linkitApi.post("/page/blocks/reorder", { ids: next.map((b) => b.id) }); setPage(data); } catch { load(); }
  };

  // ---- publish
  const publish = async () => {
    setPublishing(true);
    try { const { data } = await linkitApi.post("/page/publish"); setPage(data); toast.success("Published! Your page is live."); }
    catch { toast.error("Publish failed"); } finally { setPublishing(false); }
  };
  const discard = async () => {
    if (!window.confirm("Discard all unpublished changes?")) return;
    const { data } = await linkitApi.post("/page/discard"); setPage(data); toast.success("Draft discarded");
  };

  const publicUrl = page ? `${window.location.origin}/u/${page.username}` : "";

  if (loading || !page) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0b0c10] text-white"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0b0c10] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-[#0b0c10]/90 px-4 py-3 backdrop-blur sm:px-6">
        <div className="flex items-center gap-2 font-black"><Zap size={20} fill="white" /> Linkit</div>
        <div className="flex items-center gap-2">
          {page.has_draft && <span data-testid="draft-badge" className="hidden rounded-full bg-amber-500/20 px-3 py-1 text-xs font-semibold text-amber-300 sm:inline">Unpublished changes</span>}
          <button onClick={() => setAiOpen(true)} data-testid="open-ai" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-3 py-2 text-xs font-bold sm:px-4"><Sparkles size={14} /> AI Builder</button>
          {page.has_draft && <button onClick={discard} data-testid="discard-btn" className="rounded-full p-2 text-white/60 hover:bg-white/5" title="Discard changes"><Undo2 size={16} /></button>}
          <button onClick={publish} disabled={publishing} data-testid="publish-btn" className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500 px-4 py-2 text-xs font-bold hover:bg-indigo-400 disabled:opacity-60">{publishing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} Publish</button>
          <button onClick={() => { logout(); nav("/login"); }} className="rounded-full p-2 text-white/60 hover:bg-white/5"><LogOut size={16} /></button>
        </div>
      </header>

      <div className={`mx-auto grid max-w-7xl grid-cols-1 gap-6 p-4 sm:p-6 ${tab === "editor" ? "lg:grid-cols-[1fr_400px]" : ""}`}>
        {/* Editor column */}
        <div className="space-y-5">
          {/* Email verification banner — only shows for unverified accounts. */}
          <VerifyBanner />
          {/* Mobile app promo strip — dismissable, once per user. */}
          <MobileAppStrip />
          {/* Tabs */}
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1 text-xs font-semibold">
            {[
              { k: "editor", label: "Editor", Icon: LayoutGrid },
              { k: "analytics", label: "Analytics", Icon: BarChart3 },
              { k: "leads", label: "Leads", Icon: Users },
              { k: "settings", label: "Settings", Icon: SettingsIcon },
            ].map((t) => {
              const active = tab === t.k;
              return (
                <button
                  key={t.k}
                  data-testid={`linkit-tab-${t.k}`}
                  onClick={() => setTab(t.k)}
                  className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-2 transition ${active ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  <t.Icon size={13} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "editor" && (
            <>
          {/* public link */}
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <span className="truncate text-sm text-white/70">{publicUrl}</span>
            <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copied"); }} className="ml-auto rounded-lg p-2 hover:bg-white/5"><Copy size={15} /></button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="rounded-lg p-2 hover:bg-white/5"><ExternalLink size={15} /></a>
            <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${page.published ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/50"}`}>{page.published ? "Live" : "Draft"}</span>
          </div>

          {/* Profile */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <h2 className="mb-3 text-sm font-bold text-white/80">Profile</h2>
            <div className="flex items-center gap-4">
              <button onClick={editExistingAvatar} title={profile.avatar_url ? "Click to re-crop your photo" : "Upload a photo"} className="group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-white/10">
                {profile.avatar_url ? (
                  <>
                    <img src={resolveImg(profile.avatar_url)} alt="" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 hidden items-center justify-center bg-black/60 text-[10px] font-bold text-white group-hover:flex">Re-crop</span>
                  </>
                ) : <Upload size={18} className="text-white/50" />}
                {avatarBusy && <div className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 size={16} className="animate-spin" /></div>}
              </button>
              <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
              <div className="flex-1 space-y-2">
                <input data-testid="profile-name" value={profile.display_name} onChange={(e) => saveProfile({ ...profile, display_name: e.target.value })} placeholder="Display name" className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                {profile.avatar_url && (
                  <button onClick={() => avatarRef.current?.click()} data-testid="replace-avatar" className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-white">
                    <Upload size={11} /> Replace photo
                  </button>
                )}
              </div>
            </div>
            <textarea data-testid="profile-bio" value={profile.bio} onChange={(e) => saveProfile({ ...profile, bio: e.target.value })} placeholder="Short bio" rows={2} className="mt-3 w-full resize-none rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] p-2">
              <ImageDown size={16} className="shrink-0 text-white/40" />
              <input data-testid="grab-avatar-url" value={grabUrl} onChange={(e) => setGrabUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); grabAvatar(); } }} placeholder="Paste an Instagram / YouTube / X link to grab your photo" className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-white/30" />
              <button onClick={grabAvatar} disabled={grabbing || !grabUrl.trim()} data-testid="grab-avatar-btn" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold hover:bg-white/20 disabled:opacity-40">
                {grabbing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Grab photo
              </button>
            </div>
          </section>

          {/* Theme */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <h2 className="mb-3 text-sm font-bold text-white/80">Appearance</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ColorInput label="Accent" value={theme.accent || "#6366f1"} onChange={(v) => saveTheme({ accent: v })} />
              <ColorInput label="Background" value={theme.bg_color || "#0f1117"} onChange={(v) => saveTheme({ bg_color: v })} />
              <ColorInput label="Gradient to" value={theme.bg_gradient_to || "#1e1b2e"} onChange={(v) => saveTheme({ bg_gradient_to: v })} />
              <ColorInput label="Text" value={theme.text_color || "#ffffff"} onChange={(v) => saveTheme({ text_color: v })} />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-white/60">Background</span>
                <Select value={theme.bg_style || "gradient"} onValueChange={(v) => saveTheme({ bg_style: v })}>
                  <SelectTrigger className="h-8 w-32 text-xs text-black"><SelectValue /></SelectTrigger>
                  <SelectContent>{["solid", "gradient"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-white/60">Buttons</span>
                <Select value={theme.button_style || "pill"} onValueChange={(v) => saveTheme({ button_style: v })}>
                  <SelectTrigger className="h-8 w-32 text-xs text-black"><SelectValue /></SelectTrigger>
                  <SelectContent>{["pill", "soft", "sharp"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2 sm:col-span-2">
                <span className="text-xs text-white/60">Font</span>
                <Select value={theme.font || "Inter"} onValueChange={(v) => saveTheme({ font: v })}>
                  <SelectTrigger className="h-8 w-44 text-xs text-black"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Blocks */}
          <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-white/80">Blocks</h2>
              <div className="relative">
                <button onClick={() => setAddOpen((o) => !o)} data-testid="add-block-btn" className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500 px-3 py-1.5 text-xs font-semibold hover:bg-indigo-400"><Plus size={14} /> Add item</button>
                {addOpen && (
                  <div className="absolute right-0 z-30 mt-1 w-64 overflow-hidden rounded-xl border border-black/10 bg-white p-1 text-black shadow-xl">
                    <p className="border-b border-black/5 px-3 pb-1.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-black/40">What are you adding?</p>
                    {[
                      { key: "link", hint: "Any URL — we'll pull the image, title & description." },
                      { key: "lead_form", hint: "Collect contact / consultation / brand enquiries." },
                      { key: "section", hint: "Group items under a heading (e.g. Travel, Products)." },
                    ].map(({ key, hint }) => {
                      const def = BLOCK_TYPES[key];
                      if (!def) return null;
                      const Icon = def.icon;
                      return (
                        <button key={key} onClick={() => addBlock(key)} data-testid={`add-block-${key}`} className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left hover:bg-black/5">
                          <Icon size={16} className="mt-0.5 shrink-0 text-indigo-500" />
                          <div className="min-w-0"><p className="text-sm font-semibold">{def.label}</p><p className="text-[11px] text-black/45">{hint}</p></div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="rounded-xl bg-white/[0.02] p-2">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={(content.blocks || []).map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {(content.blocks || []).map((b) => (
                      <BlockRow key={b.id} block={b} onEdit={() => setEditing(b)} onDelete={() => deleteBlock(b.id)} onToggle={() => toggleBlock(b)} />
                    ))}
                    {!content.blocks?.length && <p className="py-6 text-center text-sm text-white/40">No items yet. Click &quot;Add item&quot; to start.</p>}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </section>
            </>
          )}

          {tab === "analytics" && <LinkitAnalytics />}
          {tab === "leads" && <LeadsPanel />}
          {tab === "settings" && <SettingsPanel />}
        </div>

        {/* Preview column (only visible on editor tab) */}
        {tab === "editor" && (
          <div className="lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)]">
            <div className="mx-auto h-full max-w-[380px] overflow-hidden rounded-[2.5rem] border-[6px] border-black/60 bg-black shadow-2xl">
              <div className="h-full overflow-y-auto">
                <PagePreview content={content} username={page.username} preview />
              </div>
            </div>
          </div>
        )}
      </div>

      <BlockEditor open={!!editing} block={editing} onClose={() => setEditing(null)} onSave={saveBlock} />
      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} onApplied={(data) => setPage(data)} />
      {avatarCropSrc && (
        <ImageCropperModal src={avatarCropSrc} round defaultAspect={1} onCancel={() => setAvatarCropSrc(null)} onCropped={onAvatarCropped} />
      )}
    </div>
  );
}
